import { stemPhrase } from './text'
import type { Bucket, Locale, Measure, PeriodKey } from './types'

/**
 * CUVINTELE FIXE ALE BAREI, in romana si engleza.
 *
 * Restul vocabularului - seturile, campurile si sinonimele lor - vine din
 * catalogul serverului (`Catalog.php`), iar valorile (sediile, tehnicienii,
 * aplicatiile) din baza, numai din compania aleasa. Aici stau doar cuvintele
 * de legatura: timpul, socotelile, "pe", "fara", "top".
 *
 * Totul se compara pe radacini (`text.ts`), deci formele articulate si de
 * plural nu trebuie scrise de mana.
 */

const stems = (words: string[]): Set<string> => new Set(words.map(stemPhrase))

/** Cuvinte care nu poarta inteles in fraza. */
export const STOP = stems([
  'de', 'a', 'al', 'ale', 'ai', 'cu', 'din', 'in', 'la', 'si', 'sau', 'e', 'este', 'sunt', 'au', 'are', 'avem',
  'imi', 'mi', 'vreau', 'sa', 'vad', 'as', 'dori', 'te', 'rog', 'toate', 'toti', 'tot', 'un', 'o', 'unui', 'unei',
  'pentru', 'meu', 'mea', 'mele', 'nostru', 'noastra', 'noastre', 'fost', 'cel', 'cea', 'cei', 'cele', 'acum',
  'doar', 'numai', 'exclusiv', 'inclusiv', 'despre', 'catre', 'spre', 'pe', 'per', 'ce', 'care', 'cine', 'unde',
  'instalat', 'instalata', 'instalate', 'aflat', 'aflate', 'exista', 'raport', 'raportul', 'date', 'datele',
  'the', 'an', 'of', 'on', 'at', 'for', 'with', 'me', 'my', 'all', 'are', 'is', 'was', 'were', 'there', 'please',
  'give', 'get', 'do', 'does', 'have', 'has', 'to', 'from', 'only', 'just', 'by', 'which', 'what', 'who', 'where',
  'installed', 'report', 'data', 'and', 'or', 'that', 'it', 'its', 'any', 'how', 'per', 'about', 'in', 'into',
  's', 'sint', 'fie', 'ma', 'va', 'putea', 'poti', 'lui', 'lu', 's-a', 'sa', 'a', 'au', 'dupa', 'by',
  /* Umplutura de intrebare: "what is", "show me", "give me", "da-mi", "item (ci)". */
  'i', 'we', 'our', 'us', 'you', 'can', 'could', 'would', 'like', 'want', 'see', 'tell', 'let', 'need', 'whats',
  'be', 'been', 'did', 'had', 'got', 'item', 'items', 'thing', 'things', 'stuff',
  'da-mi', 'spune-mi', 'stiu', 'aici', 'nostri', 'element', 'elemente', 'lucruri', 'chestii', 'oare',
  'am', 'ati', 'much', 'we', 'did',
])

/** "fara", "nu" - neaga ce urmeaza. */
export const NEGATION = stems(['fara', 'nu', 'not', 'without', 'except', 'excluding', 'mai putin', 'non'])

/** Cer o lista, nu o cifra. */
export const LIST_WORDS = stems([
  'lista', 'listeaza', 'arata', 'afiseaza', 'detalii', 'list', 'show', 'display', 'details', 'pe ce', 'which',
  'care sunt', 'ce sunt', 'tabel', 'table', 'arata-mi', 'afiseaza-mi', 'listeaza-mi', 'show me', 'list of',
])

/**
 * "NOU": randurile CREATE in perioada. Cuvantul muta perioada pe campul de
 * creare al setului (vezi `CREATION_FIELDS`); fara perioada in fraza, se iau
 * ultimele 30 de zile si se vad in bucatile definitiei.
 *
 * Cele tari ("new", "nou") bat si valorile: "new tickets" sunt tichetele
 * create de curand, nu cele cu starea "New" - aia se cere cu "status new".
 * Participiile ("added", "adaugate", "descoperite") lasa intai vocabularul
 * setului sa le ia: "relatii descoperite" e originea relatiei, iar "ci
 * adaugate" e campul "Adaugat".
 */
export const FRESH_WORDS = stems([
  'new', 'newly', 'newest', 'recent', 'recently', 'latest',
  'nou', 'noi', 'noua', 'noile', 'noul', 'noii', 'recente', 'recenti', 'recenta', 'proaspete', 'proaspat',
])
export const CREATED_WORDS = stems([
  'added', 'created', 'opened', 'appeared', 'discovered', 'newly added', 'recently added', 'first seen',
  'adaugat', 'adaugate', 'adaugata', 'adaugati', 'creat', 'create', 'creata', 'creati',
  'aparut', 'aparute', 'aparuta', 'aparuti', 'descoperit', 'descoperite', 'descoperita', 'descoperiti',
])

/**
 * Campul de creare al unui set: primul camp de tip data cu una dintre chei,
 * in ordinea asta. Un set fara niciunul (instantanee zilnice, costuri pe
 * luna, imprimante, licente) nu are "nou": se spune, nu se ghiceste.
 */
export const CREATION_FIELDS = ['created', 'added', 'first_seen', 'first_detected', 'detected', 'started', 'logged_in']

/**
 * "SCHIMBAT": ce s-a modificat. Se intelege numai unde exista un set de
 * schimbari pentru setul numit (`CHANGES_OF`): "ce s-a schimbat in cmdb" ->
 * schimbarile CI. Substantivele ("schimbari", "changes") muta si ele fraza,
 * dar numai cand setul de baza e numit.
 */
export const CHANGED_WORDS = stems([
  'changed', 'modified', 'updated', 'altered', 'changes', 'modifications', 'updates',
  'schimbat', 'schimbate', 'schimbata', 'schimbati', 'modificat', 'modificate', 'modificata', 'modificati',
  'actualizat', 'actualizate', 'actualizata', 'schimbari', 'schimbarile', 'modificari', 'modificarile',
])
export const CHANGES_OF: Record<string, string> = {
  'cmdb.ci': 'cmdb.changes',
}

/**
 * Cuvintele fixe englezesti, ca sugestiile si completarea sa ramana in limba
 * aleasa: in engleza nu se propune "luna trecuta", in romana nu "last month".
 * Cuvintele comune ("max", "total", "%") merg in amandoua.
 */
const EN_FIXED = new Set([
  'today', 'yesterday', 'this', 'last', 'previous', 'past', 'next', 'week', 'weeks', 'month', 'months', 'quarter',
  'quarters', 'year', 'years', 'day', 'days', 'daily', 'weekly', 'monthly', 'quarterly', 'yearly', 'average', 'mean',
  'sum', 'maximum', 'longest', 'minimum', 'percentage', 'percent', 'rate', 'ratio', 'compliance',
])
const BOTH_FIXED = new Set(['max', 'min', 'total', '%', 'avg'])

/** In ce limba e o expresie fixa (timp, socoteala, felie): 'ro', 'en' sau amandoua. */
export function fixedLocale(phrase: string): Locale | 'both' {
  const words = phrase.toLowerCase().split(' ')
  if (words.every((w) => BOTH_FIXED.has(w))) return 'both'
  if (words.every((w) => EN_FIXED.has(w) || BOTH_FIXED.has(w))) return 'en'
  return 'ro'
}

/** Cer o numarare. */
export const COUNT_WORDS = stems(['cate', 'cati', 'numarul', 'numar', 'nr', 'count', 'how many', 'number of', 'volum', 'volume'])

export const AGG_WORDS: [string, Measure['agg']][] = [
  ['media', 'avg'], ['medie', 'avg'], ['mediu', 'avg'], ['average', 'avg'], ['avg', 'avg'], ['mean', 'avg'],
  ['suma', 'sum'], ['sum', 'sum'], ['totalul', 'sum'], ['total', 'sum'],
  ['maxim', 'max'], ['maximum', 'max'], ['max', 'max'], ['cel mai mare', 'max'], ['cea mai mare', 'max'], ['longest', 'max'],
  ['minim', 'min'], ['minimum', 'min'], ['min', 'min'], ['cel mai mic', 'min'], ['cea mai mica', 'min'],
  ['procent', 'ratio'], ['procentul', 'ratio'], ['%', 'ratio'], ['rata', 'ratio'], ['percentage', 'ratio'],
  ['percent', 'ratio'], ['rate', 'ratio'], ['ratio', 'ratio'], ['conformitate', 'ratio'], ['compliance', 'ratio'],
]

/** Cuvintele care se pot pune in mijlocul numelui unui camp: "timp MEDIU de rezolvare". */
export const AGG_INFIX: [string, Measure['agg']][] = [
  ['mediu', 'avg'], ['medie', 'avg'], ['average', 'avg'], ['total', 'sum'], ['totala', 'sum'], ['maxim', 'max'], ['maxima', 'max'], ['minim', 'min'], ['minima', 'min'],
]

/** "pe sediu", "by office", "dupa tehnician". */
export const GROUP_MARKERS = stems(['pe', 'per', 'by', 'dupa', 'grupat pe', 'grupate pe', 'pentru fiecare', 'pe fiecare', 'for each', 'each', 'impartit pe', 'split by', 'grouped by'])

export const BUCKETS: [string, Bucket][] = [
  ['zi', 'day'], ['zile', 'day'], ['zilnic', 'day'], ['day', 'day'], ['days', 'day'], ['daily', 'day'],
  ['saptamana', 'week'], ['saptamani', 'week'], ['saptamanal', 'week'], ['week', 'week'], ['weeks', 'week'], ['weekly', 'week'],
  ['luna', 'month'], ['luni', 'month'], ['lunar', 'month'], ['month', 'month'], ['months', 'month'], ['monthly', 'month'],
  ['trimestru', 'quarter'], ['trimestre', 'quarter'], ['trimestrial', 'quarter'], ['quarter', 'quarter'], ['quarters', 'quarter'], ['quarterly', 'quarter'],
  ['an', 'year'], ['ani', 'year'], ['anual', 'year'], ['year', 'year'], ['years', 'year'], ['yearly', 'year'],
]

/** Timpul, ca expresii intregi. */
export const TIME_PHRASES: [string, PeriodKey][] = [
  ['azi', 'today'], ['astazi', 'today'], ['today', 'today'], ['ziua de azi', 'today'],
  ['ieri', 'yesterday'], ['yesterday', 'yesterday'],
  ['saptamana asta', 'this_week'], ['saptamana aceasta', 'this_week'], ['saptamana curenta', 'this_week'], ['this week', 'this_week'],
  ['saptamana trecuta', 'last_week'], ['last week', 'last_week'], ['previous week', 'last_week'],
  ['luna asta', 'this_month'], ['luna aceasta', 'this_month'], ['luna curenta', 'this_month'], ['this month', 'this_month'],
  ['luna trecuta', 'last_month'], ['last month', 'last_month'], ['previous month', 'last_month'], ['luna precedenta', 'last_month'],
  ['trimestrul asta', 'this_quarter'], ['trimestrul acesta', 'this_quarter'], ['trimestrul curent', 'this_quarter'], ['this quarter', 'this_quarter'],
  ['trimestrul trecut', 'last_quarter'], ['last quarter', 'last_quarter'], ['previous quarter', 'last_quarter'],
  ['anul asta', 'this_year'], ['anul acesta', 'this_year'], ['anul curent', 'this_year'], ['this year', 'this_year'],
  ['anul trecut', 'last_year'], ['last year', 'last_year'], ['previous year', 'last_year'],
  ['ultima saptamana', 'last_7_days'], ['ultima luna', 'last_30_days'],
  ['past week', 'last_7_days'], ['past month', 'last_30_days'],
  ['luna viitoare', 'next_month'], ['luna urmatoare', 'next_month'], ['next month', 'next_month'],
  ['saptamana viitoare', 'next_7_days'], ['next week', 'next_7_days'],
]

/** "ultimele N zile", "last N days". */
export const LAST_WORDS = stems(['ultimele', 'ultimii', 'ultima', 'last', 'past', 'previous'])

/** "urmatoarele 30 de zile", "next 30 days" - expirari. */
export const NEXT_WORDS = stems(['urmatoarele', 'urmatorii', 'next', 'coming'])

export const DAY_WORDS = stems(['zile', 'zi', 'days', 'day'])

export const WEEK_WORDS = stems(['saptamani', 'saptamana', 'weeks', 'week'])

export const MONTH_WORDS = stems(['luni', 'luna', 'months', 'month'])

/**
 * CONDITIILE NUMERICE: "peste 8 ore", "mai mult de 3 zile", "sub 50%".
 * Comparatia se citeste inainte de restul cuvintelor, deci "mai putin de"
 * nu ajunge sa fie luat drept negatie.
 */
export const COMPARATORS: [string, 'gt' | 'gte' | 'lt' | 'lte'][] = [
  ['peste', 'gt'], ['mai mult de', 'gt'], ['mai mult decat', 'gt'], ['mai mare de', 'gt'], ['mai mare decat', 'gt'], ['mai mari de', 'gt'],
  ['over', 'gt'], ['more than', 'gt'], ['above', 'gt'], ['greater than', 'gt'], ['longer than', 'gt'], ['bigger than', 'gt'],
  ['sub', 'lt'], ['mai putin de', 'lt'], ['mai putin decat', 'lt'], ['mai mic de', 'lt'], ['mai mica de', 'lt'], ['mai mici de', 'lt'],
  ['under', 'lt'], ['less than', 'lt'], ['below', 'lt'], ['shorter than', 'lt'], ['fewer than', 'lt'],
  ['cel putin', 'gte'], ['at least', 'gte'],
  ['mai vechi de', 'gt'], ['mai vechi decat', 'gt'], ['older than', 'gt'],
  ['mai noi de', 'lt'], ['mai noi decat', 'lt'], ['newer than', 'lt'], ['younger than', 'lt'],
  ['cel mult', 'lte'], ['at most', 'lte'],
]

/** Unitatile de dupa numar: secunde pentru durate, octeti pentru marimi. */
export const UNITS: [string, 'duration' | 'bytes' | 'percent', number][] = [
  ['secunda', 'duration', 1], ['secunde', 'duration', 1], ['sec', 'duration', 1], ['seconds', 'duration', 1], ['second', 'duration', 1],
  ['minut', 'duration', 60], ['minute', 'duration', 60], ['minutes', 'duration', 60], ['mins', 'duration', 60],
  ['ora', 'duration', 3600], ['ore', 'duration', 3600], ['h', 'duration', 3600], ['hours', 'duration', 3600], ['hour', 'duration', 3600],
  ['zi', 'duration', 86400], ['zile', 'duration', 86400], ['days', 'duration', 86400], ['day', 'duration', 86400],
  ['kb', 'bytes', 1024], ['mb', 'bytes', 1024 * 1024], ['gb', 'bytes', 1024 * 1024 * 1024], ['tb', 'bytes', 1024 * 1024 * 1024 * 1024],
  ['%', 'percent', 1], ['procent', 'percent', 1], ['procente', 'percent', 1], ['percent', 'percent', 1],
]

/**
 * Cand o conditie de timp nu spune pe ce camp ("tichete deschise de peste 3
 * zile"), se alege dupa celelalte filtre: deschise -> vechimea, inchise ->
 * timpul de rezolvare. Altfel: campul unic de tip durata, sau o intrebare.
 */
/** Comparatiile care spun singure campul: "mai vechi de 7 zile" e vechimea. */
export const COMPARATOR_FIELDS: [string, string][] = [
  ['mai vechi de', 'age'], ['mai vechi decat', 'age'], ['older than', 'age'],
  ['mai noi de', 'age'], ['mai noi decat', 'age'], ['newer than', 'age'], ['younger than', 'age'],
]

/** "cat", "how much": suma setului cand are una (bani, ore), altfel numarul. */
export const HOW_MUCH = stems(['how much', 'cat'])

export const DURATION_HINTS: { field: string; value: number; duration: string }[] = [
  { field: 'closed', value: 0, duration: 'age' },
  { field: 'closed', value: 1, duration: 'resolution_time' },
]

/** "top 10", "primele 5", "cele mai multe". */
export const TOP_WORDS = stems(['top', 'primele', 'primii', 'first', 'cele mai multe', 'cei mai multi', 'cele mai', 'cei mai', 'most'])

export const MONTHS: [string[], number][] = [
  [['ianuarie', 'january', 'ian', 'jan'], 1],
  [['februarie', 'february', 'feb'], 2],
  [['martie', 'march', 'mar'], 3],
  [['aprilie', 'april', 'apr'], 4],
  [['mai', 'may'], 5],
  [['iunie', 'june', 'iun', 'jun'], 6],
  [['iulie', 'july', 'iul', 'jul'], 7],
  [['august', 'aug'], 8],
  [['septembrie', 'september', 'sep', 'sept'], 9],
  [['octombrie', 'october', 'oct'], 10],
  [['noiembrie', 'november', 'noi', 'nov'], 11],
  [['decembrie', 'december', 'dec'], 12],
]

/** "intre X si Y", "de la X pana la Y", "between X and Y". */
export const RANGE_OPEN = stems(['intre', 'between', 'de la', 'from'])
export const RANGE_JOIN = stems(['si', 'and', 'pana la', 'pana in', 'pana', 'to', 'until', 'through'])

/**
 * Contrariile campurilor da/nu: "deschise" inseamna `closed = 0`, "lipsa"
 * inseamna `installed = 0`. Se aplica numai pe setul care are campul.
 */
export const ANTONYMS: { words: string[]; field: string; value: 0 | 1 }[] = [
  { words: ['deschis', 'deschise', 'deschisi', 'nerezolvat', 'nerezolvate', 'open', 'active', 'unresolved', 'in lucru'], field: 'closed', value: 0 },
  { words: ['lipsa', 'lipsesc', 'lipseste', 'neinstalat', 'neinstalate', 'neaplicate', 'missing', 'pending', 'not installed'], field: 'installed', value: 0 },
  { words: ['incalcat', 'incalcate', 'depasit', 'depasite', 'incalcari', 'incalcare', 'nerespectat', 'breached', 'breach', 'breaches', 'violated', 'late', 'overdue'], field: 'sla_met', value: 0 },
  { words: ['la zi', 'up to date', 'aplicate', 'applied'], field: 'installed', value: 1 },
  { words: ['fizice', 'fizic', 'physical'], field: 'virtual', value: 0 },
  { words: ['online', 'functionale', 'functional', 'functioneaza', 'disponibile', 'pornite', 'up', 'running'], field: 'offline', value: 0 },
  { words: ['neverificate', 'neverificat', 'unverified'], field: 'verified', value: 0 },
  { words: ['nepublicate', 'nepublicat', 'unpublished'], field: 'published', value: 0 },
  { words: ['disparute', 'disparuta', 'disappeared', 'gone'], field: 'current', value: 0 },
  { words: ['neautorizate', 'neautorizata', 'unauthorized', 'unauthorised'], field: 'authorized', value: 0 },
]

/**
 * Seturile pe care bara le poate alege si cand fraza NU le numeste ("cate
 * servere avem", "Bucuresti", "media pe sediu"). Restul catalogului (ITIL,
 * CMDB, costuri, Securitee, Productivitee...) se alege numai cand e numit:
 * "pe sediu" sau "Not seen" n-au voie sa traga fraza intr-un set la care omul
 * nu s-a gandit.
 */
export const UNNAMED_SETS = new Set([
  'easeedesk.tickets', 'easeedesk.sla', 'inventoree.devices', 'softsee.installed_apps',
  'opteemum.os_updates', 'devices.logins', 'inventoree.installed_printers',
])

/** Seturile care le rafineaza pe altele: SLA e o privire peste tichete. */
export const REFINES: Record<string, string> = {
  'easeedesk.sla': 'easeedesk.tickets',
  'easeedesk.workload': 'easeedesk.tickets',
  'costs.by_client': 'costs.by_service',
  'costs.consumption': 'costs.by_service',
}

/**
 * Campul de timp pe care il schimba un filtru: "tichete REZOLVATE ieri"
 * inseamna rezolvate ieri, nu create ieri.
 */
export const TIME_FIELD_HINTS: { field: string; value: number; timeField: string }[] = [
  { field: 'closed', value: 1, timeField: 'resolved' },
  { field: 'installed', value: 1, timeField: 'installed_at' },
]

/** Dimensiunea de restrangere a unui camp cu id. */
export const DIM_OF_FIELD: Record<string, 'office' | 'person' | 'group' | 'device'> = {
  office: 'office',
  assignee: 'person',
  group: 'group',
  user: 'person',
}

/** Nume in plus pentru seturi, cum le scrie omul: "cis", "cmdb records". */
export const SET_NAMES: Record<string, string[]> = {
  'cmdb.ci': ['cis', 'cmdb items', 'cmdb entries', 'cmdb records', 'configuration items', 'inregistrari cmdb'],
  /*
   * FACTURAREA. Portalul nu tine facturi si nici un semn "facturabil" pe
   * tichete sau pe timpul lucrat. Ce se factureaza e chargeback-ul (luna
   * inghetata, pe serviciu si pe directie, cu tichetele si elementele din care
   * iese suma) si timpul lucrat inmultit cu tariful orar (costul pe
   * tehnician). Acolo duc cuvintele de facturare; "nefacturat" si
   * "facturabil" nu au date si se spune (`BILLABLE_WORDS`).
   */
  'costs.consumption': [
    'facturat', 'facturata', 'facturate', 'facturati', 'facturare', 'facturarea', 'factura', 'facturi', 'refacturat',
    'suma facturata', 'valoare facturata',
    'billed', 'bill', 'billing', 'invoiced', 'invoice', 'invoices', 'invoicing', 'charged', 'charges', 'recharged',
  ],
  'costs.technicians': [
    'ore lucrate', 'orele lucrate', 'hours worked', 'tarif orar', 'tariful orar', 'tarif', 'tariful', 'tarife', 'hourly rate', 'hourly rates',
    'ore facturate', 'ore facturabile', 'timp facturat', 'timp facturabil', 'timpul facturat', 'timpul facturabil',
    'billable hours', 'billed hours', 'hours billed', 'billable time', 'billed time', 'time billed',
  ],
  'costs.by_device': ['echipamente dupa cost', 'aparate dupa cost', 'calculatoare dupa cost', 'devices by cost', 'assets by cost', 'servers by cost'],
  'costs.service_requests': [
    'pret', 'preturi', 'pret unitar', 'price', 'prices', 'unit price', 'unit prices',
    'cereri din catalog', 'cererile din catalog', 'catalog requests',
  ],
}

/**
 * Nume in plus pentru campuri, cu socoteala lor cand o poarta numele:
 * "tichete facturate" e suma tichetelor din chargeback, nu un filtru.
 */
export const FIELD_NAMES: { set: string; field: string; words: string[]; agg?: Measure['agg'] }[] = [
  { set: 'costs.consumption', field: 'ticket_count', agg: 'sum', words: ['tichete facturate', 'billed tickets', 'tickets billed'] },
  { set: 'costs.consumption', field: 'amount', words: ['amount billed', 'billed amount', 'suma facturata', 'valoare facturata'] },
  {
    set: 'costs.technicians', field: 'hours', agg: 'sum',
    words: ['ore lucrate', 'orele lucrate', 'hours worked', 'ore facturate', 'ore facturabile', 'timp facturat', 'timp facturabil', 'timpul facturat', 'timpul facturabil', 'billable hours', 'billed hours', 'hours billed', 'billable time', 'billed time', 'time billed'],
  },
  { set: 'costs.service_requests', field: 'unit_price', agg: 'avg', words: ['pret', 'preturi', 'price', 'prices', 'unit prices', 'preturile'] },
]

/** Cuvintele de facturare, pentru explicatia de pe seturile care n-au bani. */
export const BILLING_WORDS = stems([
  'facturat', 'facturata', 'facturate', 'facturati', 'facturare', 'factura', 'facturi', 'refacturat',
  'billed', 'bill', 'billing', 'invoiced', 'invoice', 'invoices', 'invoicing', 'charged', 'charges',
  /* Banii in general: pe un set fara cost se spune unde sunt. */
  'cost', 'costul', 'costuri', 'costurile', 'pret', 'pretul', 'preturi', 'tarif', 'tariful',
  'costs', 'price', 'prices', 'rate per hour', 'hourly rate',
])

/**
 * "Facturabil", "nefacturat": portalul nu tine semnul asta nicaieri. Nu se
 * ghiceste ("toate tichetele"), se spune ca nu exista date.
 */
export const BILLABLE_WORDS = stems([
  'facturabil', 'facturabile', 'facturabila', 'facturabili', 'nefacturat', 'nefacturate', 'nefacturata', 'nefacturati',
  'nefacturabil', 'nefacturabile', 'nefacturabila', 'de facturat', 'nu s-a facturat',
  'billable', 'non-billable', 'nonbillable', 'unbillable', 'unbilled', 'not billed', 'non billable', 'to be billed', 'to bill',
  'chargeable', 'non-chargeable', 'unchargeable', 'not charged',
])

/**
 * "Pe firma", "per company": rapoartele sunt pe compania aleasa sus, deci un
 * set fara coloana de firma n-are ce grupa. Se spune, nu se pierde cuvantul.
 */
export const COMPANY_WORDS = stems([
  'firma', 'firme', 'firmele', 'companie', 'companii', 'companiile', 'client', 'clienti', 'clientii',
  'company', 'companies', 'customer', 'customers', 'client company', 'tenant', 'tenants',
])

/** Cuvintele de "nou" si "schimbat", propuse cand omul le-a scris gresit ("neww", "noo"). */
export const INTENT_WORDS: Record<Locale, string[]> = {
  ro: ['nou', 'noi', 'noua', 'adaugate', 'aparute', 'schimbat', 'modificate'],
  en: ['new', 'added', 'created', 'changed', 'modified'],
}
